<cs-header>
  <cs-header-title>{{ exerciseName }}</cs-header-title>
</cs-header>

<cs-content [loading]="loading">
  <div class="form-content">
    @if (settings.showSlotName && slotName) {
      <cs-form-field [label]="LL.exerciseConfiguration.labels.muscleGroup()">
        <cs-note>{{ slotName }}</cs-note>
      </cs-form-field>
      <div class="divider"></div>
    }

    @if (myoMode !== undefined) {
      <div class="form-row">
        <cs-form-field [label]="LL.exerciseConfiguration.labels.myoReps()">
          <cs-select [(value)]="myoMode" (valueChange)="handleMyoChange()">
            <cs-select-option [value]="MyoRepsMode.Avoid" [label]="LL.exerciseConfiguration.options.avoid()"></cs-select-option>
            <cs-select-option [value]="MyoRepsMode.Prefer" [label]="LL.exerciseConfiguration.options.prefer()"></cs-select-option>
            <cs-select-option [value]="MyoRepsMode.Auto" [label]="LL.exerciseConfiguration.options.auto()"></cs-select-option>
          </cs-select>
        </cs-form-field>
        <cs-form-field [label]="getVolumeLabel()">
          <cs-input-measure [(canonValue)]="numSets" type="sets" (editDone)="handleNumSetsChange()"></cs-input-measure>
        </cs-form-field>
      </div>
      @if (myorepTextLong) {
        <cs-form-field [label]="LL.exerciseConfiguration.labels.myoRepsVolume()">
          <cs-note>{{ myorepTextLong }}</cs-note>
        </cs-form-field>
      }
      <div class="divider"></div>
    }

    @if (myoMode === undefined) {
      <div class="form-row single">
        <cs-form-field [label]="LL.exerciseConfiguration.labels.volume()">
          <cs-input-measure [(canonValue)]="numSets" type="sets" (editDone)="handleNumSetsChange()"></cs-input-measure>
        </cs-form-field>
      </div>
      <div class="divider"></div>
    }

    @if (rmTestingMode !== undefined) {
      <div class="form-row single">
        <cs-form-field [label]="LL.exerciseConfiguration.labels.rmTesting()">
          <cs-select [(value)]="rmTestingMode" (valueChange)="handleRMTestingChange()">
            <cs-select-option [value]="AdvancedMethodsRMTesting.Avoid" [label]="LL.exerciseConfiguration.options.avoid()"></cs-select-option>
            <cs-select-option [value]="AdvancedMethodsRMTesting.Automatic" [label]="LL.exerciseConfiguration.options.auto()"></cs-select-option>
            <cs-select-option [value]="AdvancedMethodsRMTesting.Prefer" [label]="LL.exerciseConfiguration.options.prefer()"></cs-select-option>
          </cs-select>
        </cs-form-field>
      </div>
    }

    @if (repTargets.length > 0) {
      <div class="divider"></div>
      @if (dupTupBanner) {
        <cs-info-card
          type="info"
          [dismissible]="false"
          [message]="dupTupBanner">
        </cs-info-card>
      }
      <div class="form-row rep-targets" [class.single]="repTargets.length === 1" [class.coach-inline]="!!coach">
        @for (repTarget of repTargets; track repTarget; let i = $index) {
          <cs-form-field [label]="repTarget.label">
            @if (repTarget.isEstablished) {
              <cs-input-measure
                [(canonValue)]="repTarget.targetReps"
                type="reps"
                (editDone)="handleRepTargetChange(i)">
              </cs-input-measure>
              @if (repTarget.validationError) {
                <cs-text-alert type="error" [message]="repTarget.validationError"></cs-text-alert>
              } @else if (repTarget.constraintHint) {
                <cs-note>{{ repTarget.constraintHint }}</cs-note>
              }
            }
            @if (!repTarget.isEstablished) {
              <div class="tbd-field">
                {{ LL.exerciseConfiguration.labels.toBeDetermined() }}
              </div>
            }
          </cs-form-field>
        }
      </div>
      @if (repTargetInfoMessage) {
        <cs-info-card
          type="info"
          [dismissible]="false">
          {{ repTargetInfoMessage }}
          <help-link helpId="@help:references.individualized-rep-targets">{{ LL.common.buttons.learnMore() }}</help-link>
        </cs-info-card>
      }
    }

    @if (coach) {
      <!-- #6428: the exercise editor, folded under Advanced. design-docs/coach-exercise-editing.md -->
      <div class="divider"></div>
      <div class="advanced-section">
        <cs-button tertiary icon-right [icon]="advancedOpen ? 'arrow-dropup' : 'arrow-dropdown'" (click)="toggleAdvanced()">
          {{ LL.exerciseConfiguration.coach.advanced() }}
        </cs-button>
      </div>
    }
    @if (coach && advancedOpen) {
      <cs-form-field class="coach-intensity">
        <div class="coach-label-row">
          <cs-label>{{ LL.exerciseConfiguration.coach.intensity() }}</cs-label>
          <help-icon name="info" size="x-small" helpId="@help:references.periodization-intensity"></help-icon>
        </div>
        <cs-segment [selection]="coach.scheme" [disabled]="coach.historyUnreadable" (selectionChange)="editor?.handleSchemeChange($event)">
          @for (option of coach.schemeOptions; track option.scheme) {
            <cs-segment-button [value]="option.scheme">{{ option.label }}</cs-segment-button>
          }
        </cs-segment>
        @if (coach.historyUnreadable) {
          <cs-text-alert type="error" [message]="LL.exerciseConfiguration.coach.historyUnreadable()"></cs-text-alert>
        }
      </cs-form-field>
      <div class="divider"></div>
      <div class="coach-block">
        <div class="coach-overline">{{ LL.exerciseConfiguration.coach.nextSessionOnly() }}</div>
        @if (coach.pendingTest) {
          <cs-note>{{ LL.exerciseConfiguration.coach.strengthTestNext() }}</cs-note>
          <cs-button secondary medium [label]="LL.exerciseConfiguration.coach.calculateWeights()"
            [disabled]="coach.historyUnreadable" (click)="editor?.handleCalculateWeights()"></cs-button>
        } @else {
          @for (row of coach.nextRows; track row.level) {
            @if (row.label) {
              <div class="day-label">
                {{ row.label }}
                @if (row.comesNextTooltip) {
                  <cs-tooltip iconType="info" [text]="row.comesNextTooltip"></cs-tooltip>
                }
              </div>
            }
            <div class="form-row">
                <cs-form-field [label]="LL.exerciseConfiguration.coach.nextWeight()">
                  <cs-input-measure [(canonValue)]="row.weight_kg" type="addedweight_kg" (editDone)="editor?.handleNextWeightChange(row)"></cs-input-measure>
                </cs-form-field>
                <cs-form-field [label]="LL.exerciseConfiguration.coach.nextMinReps()">
                  <cs-input-measure [(canonValue)]="row.minReps" type="reps" (editDone)="editor?.handleNextMinRepsChange(row)"></cs-input-measure>
                  @if (row.minRepsError) {
                    <cs-text-alert type="error" [message]="row.minRepsError"></cs-text-alert>
                  }
                </cs-form-field>
            </div>
          }
          <cs-note>
            {{ LL.exerciseConfiguration.coach.nextSessionNote() }}
            @if (coach.sourceNote) { {{ coach.sourceNote }} }
          </cs-note>
        }
      </div>

      <div class="divider"></div>
      <cs-form-field [label]="LL.exerciseConfiguration.coach.strengthAtLastDeload()">
        @for (row of coach.strengthRows; track $index) {
          <cs-note light>@if (row.day) { <strong>{{ row.day }}:</strong>{{ " " }} }{{ row.text }}</cs-note>
        }
      </cs-form-field>

      @for (card of coach.questionCards; track $index) {
        <cs-info-card [type]="card.type" [dismissible]="false" [message]="card.message"></cs-info-card>
      }

      <cs-form-field [label]="LL.exerciseConfiguration.coach.setAside()">
        @if (coach.setAside) {
          <cs-note light>{{ coach.setAside.text }}</cs-note>
          @if (coach.setAside.canBringBack) {
            <cs-button secondary medium [label]="LL.exerciseConfiguration.coach.bringBackNow()" (click)="editor?.handleBringBackNow()"></cs-button>
          }
        } @else {
          <cs-note>{{ LL.exerciseConfiguration.coach.setAsideNone() }}</cs-note>
        }
      </cs-form-field>

      <div class="divider"></div>
      <cs-form-field [label]="LL.exerciseConfiguration.coach.changes()">
        @if (coach.changes.length === 0) {
          <cs-note>{{ LL.exerciseConfiguration.coach.changesNone() }}</cs-note>
        } @else {
          <cs-table class="coach-changes">
            @for (change of coach.changes; track $index) {
              <cs-table-row>
                <cs-table-cell class="change-date">{{ change.date }}</cs-table-cell>
                <cs-table-cell>{{ change.text }}</cs-table-cell>
              </cs-table-row>
            }
          </cs-table>
        }
      </cs-form-field>
    }
  </div>
</cs-content>

<cs-page-action-buttons [showCancel]="true" (cancel)="handleCancel($event)">
  <cs-button primary medium [label]="LL.exerciseConfiguration.buttons.save()" [disabled]="hasValidationError" (click)="handleSave()"></cs-button>
</cs-page-action-buttons>
